<template>
  <div class="page page-with-bg page-cancelable" v-if="isLoaded">
    <div class="page-form-wrap">
      <div class="page-form-cancel">
        <b-btn class="page-cancel-button"
               variant="link"
               type="submit"
               to="/reports">
          <svg-icon name="close" /> Cancel
        </b-btn>
      </div>
      <div class="page-form-header small-header">
        <h1>Order Analysis for<span class="text-success ml-2">{{ patientName }}</span></h1>
      </div>
      <div class="page-form-content">
        <b-form @submit.prevent="submit">
          <validation-summary :errors="errors"></validation-summary>
          <b-form-group label="1. Select the type of report" class="card-selector">
            <b-form-radio v-for="(product, index) in allowedProducts"
                          v-model="report.type"
                          names="types"
                          :value="product.type"
                          :key="index">
              <svg-icon :name="product.icon" />
              <span>
                <span v-if="product.type === ProductType.CondensedRepeatedCRAAPOB" class="font-weight-bold font-italic">Condensed</span>
                {{ product.title }}
              </span>
            </b-form-radio>
            <hr />
            <legend>2. Select your lab draw option</legend>
            <b-form-checkbox id="labwork-internal"
                             name="labworkInternal"
                             class="internal-labwork"
                             v-model="report.labworkType"
                             :unchecked-value="LabworkType.Internal"
                             :value="LabworkType.Internal">
              Have labs drawn at a LabCorp PSC
            </b-form-checkbox>
            <b-form-checkbox id="labwork-by-client"
                             name="labworkByClient"
                             class="mt-2"
                             v-model="report.labworkType"
                             :unchecked-value="LabworkType.Internal"
                             :value="LabworkType.ByClient">
              Click here to handle your labs under your own LabCorp account
            </b-form-checkbox>
            <b-form-checkbox id="lab-in-clinic"
                             name="labInClinic"
                             class="mt-2"
                             v-model="report.labworkType"
                             :unchecked-value="LabworkType.Internal"
                             :value="LabworkType.ClientDraw">
              Click here to receive the lab requisition to draw in your clinic
            </b-form-checkbox>
            <v-form-group label="Expected Sample Collection Date (optional)">
              <date-picker :key="report.expectedSampleDate"
                           v-model="report.expectedSampleDate"
                           slot-scope="{ attrs, listeners }"
                           placeholder="Expected Sample Collection Date (optional)"
                           v-bind="attrs"
                           v-on="listeners" />
            </v-form-group>
            <v-form-group :validator="$v.provider"
                          label="Provider">
              <form-select :options="providerSearchOptions" v-model="provider" @input="report.providerId = $event.value" :selectedValue="defaultValue" />
            </v-form-group>
          </b-form-group>
          <b-form-group class="form-buttons">
            <b-btn variant="success" type="submit" :disabled="!report.type">Order Analysis</b-btn>
            <b-btn v-if="hasAlternateWorkflow" variant="success" @click="onOrderLabsFirst" :disabled="!report.type">Order Labs First</b-btn>
          </b-form-group>
        </b-form>
      </div>
    </div>

    <b-modal ref="confirmDifferentProductTypes"
             cancel-variant="outline-secondary"
             ok-variant="success"
             @ok="confirmSubmit"
             title="Confirm">
      Are you sure you want to change from {{ report.type === ProductType.InitialCRALDLP ? "ApoB" : "LDLP" }} to {{ report.type === ProductType.InitialCRALDLP ? "LDLP" : "ApoB"}} for this patient? You will lose consistency in the lab.
    </b-modal>
  </div>
</template>
<script>
  import { createNamespacedHelpers } from 'vuex';
  const { mapState, mapActions, mapGetters } = createNamespacedHelpers('report');

  import formMixin from "mixins/form-mixin";
  import { required } from "vuelidate/lib/validators";

  import ProductType from "enums/product-type";
  import LabworkType from "enums/labwork-type";
  import patientService from "services/patient-service";
  import employeeService from "services/employee-service";
  import clientService from "services/client-service";
  import clientType from "enums/client-type";
  import reportStatus from "enums/report-status";
  import {
    curativeClient,
    healthQuotientClient,
    alternateWorkflowOne,
    diabeticRiskAssessment,
    ldlpOrders,
    advancedCra,
    defaultOrderType,
    defaultLabworkType
  } from "@/constants";
import featureService from '../../../services/feature-service';

  export default {
    mixins: [formMixin],
    data() {
      return {
        ProductType,
        LabworkType,
        provider: null,
        providerSearchOptions: [],
        adminFeatures: [],
        patient: {
          allowedProducts: []
        },
        client: {
          clientType: null
        },
        products: [
          {
            title: "Follow-up Diabetic Risk Assessment",
            icon: "followup",
            type: ProductType.RepeatedDRA
          },
          {
            title: "Diabetic Risk Assessment",
            icon: "assessment",
            type: ProductType.InitialDRA
          },
          {
            title: "Cardiometabolic Risk Assessment with LDL-P",
            icon: "assessment",
            type: ProductType.InitialCRALDLP
          },
          {
            title: "Follow-up Cardiometabolic Risk Assessment with LDL-P",
            icon: "followup",
            type: ProductType.RepeatedCRALDLP
          },
          {
            title: "Cardiometabolic Risk Assessment with APO B",
            icon: "assessment",
            type: ProductType.InitialCRAAPOB
          },
          {
            title: "Follow-up Cardiometabolic Risk Assessment with APO B",
            icon: "followup",
            type: ProductType.RepeatedCRAAPOB
          },
          {
            title: "Follow-up Cardiometabolic Risk Assessment with APO B",
            icon: "followup",
            type: ProductType.CondensedRepeatedCRAAPOB
          },
          {
            title: "Advanced Metabolic Health Assessment",
            icon: "assessment",
            type: ProductType.AdvancedCRAAPOB
          },
          {
            title: "Follow-up Advanced Metabolic Health Assessment",
            icon: "followup",
            type: ProductType.RepeatedAdvancedCRAAPOB
          }
        ],
        isLoaded: false,
        defaultValue: 0
      };
    },

    validations: {
      provider: {
        required
      }
    },

    computed: {
      ...mapState(["report"]),
      ...mapGetters(["patientName", "hasSelectedPatient"]),

      type() { return this.report.client.type; },

      allowedProducts() {
        if (!this.hasSelectedPatient) {
          if (this.$auth.isFeatureAvailable(curativeClient) || this.$auth.isFeatureAvailable(healthQuotientClient)) {
            return this.products.filter(x => x.type == ProductType.InitialCRAAPOB);
          }

          let products = this.products.filter(x =>
            x.type != ProductType.RepeatedDRA &&
            x.type != ProductType.RepeatedCRAAPOB &&
            x.type != ProductType.RepeatedCRALDLP &&
            x.type != ProductType.CondensedRepeatedCRAAPOB &&
            x.type != ProductType.RepeatedAdvancedCRAAPOB
          );

          if (!this.$auth.isFeatureAvailable(advancedCra)) {
            products = products.filter(x => x.type != ProductType.AdvancedCRAAPOB && x.type != ProductType.RepeatedAdvancedCRAAPOB);
          }

          if (!this.$auth.isFeatureAvailable(ldlpOrders)) {
            products = products.filter(x => x.type != ProductType.InitialCRALDLP && x.type != ProductType.RepeatedCRALDLP);
          }

          if (!this.$auth.isFeatureAvailable(diabeticRiskAssessment) || this.adminFeatures.some(f => f.identifier === diabeticRiskAssessment)) {
            return products.filter(x => x.type != ProductType.InitialDRA);
          }

          return products;
        }

        return this.products.filter(x => this.patient.allowedProducts[x.type]);
      },

      currentProductTypeName() {
        return Object.keys(ProductType).find(key => ProductType[key] === this.report.type);
      },

      isAdmin() {
        return this.$auth.isInRole("Admin");
      },

      clientId() {
        return this.$route.params.clientId != null ? this.$route.params.clientId : null;
      },

      hasAlternateWorkflow() {
        if (this.adminFeatures.length) {
          return this.adminFeatures.some(f => f.identifier === alternateWorkflowOne)
        }

        return this.$auth.isFeatureAvailable(alternateWorkflowOne);
      }
    },

    async mounted() {
      await this.loadPatientData();
      await this.initSelectOptions();
      await this.loadClientData();

      this.isLoaded = true;
    },

    methods: {
      ...mapActions(["createReport"]),

      async loadPatientData() {
        if (!this.hasSelectedPatient)
          return;

        this.patient = await patientService.getById(this.report.selectedPatient.value);
      },

      async loadClientData() {
        if (this.clientId != null) {
          this.client = await clientService.get(parseInt(this.clientId));
          if (this.isAdmin) {
            this.adminFeatures = await featureService.getClientFeatures(this.clientId);
          }
        }
        else {
          this.client = await clientService.getByUserId(parseInt(this.$auth.userInfo.sub));
        }

        const defaultOrderTypeFeature = this.$auth.getFeature(defaultOrderType);
        if (defaultOrderTypeFeature && defaultOrderTypeFeature.Value) {
          const allowedProduct = this.allowedProducts.find(f => f.type.toString() === defaultOrderTypeFeature.Value);
          if (allowedProduct) {
            this.report.type = allowedProduct.type;
          }
        }

        const defaultLabworkTypeFeature = this.$auth.getFeature(defaultLabworkType);
        if (defaultLabworkTypeFeature && defaultLabworkTypeFeature.Value) {
          this.report.labworkType = parseInt(defaultLabworkTypeFeature.Value);
        }
      },

      async initSelectOptions() {
        this.providerSearchOptions = await employeeService.getProvidersAsSelectListItems(this.clientId != null ? this.clientId : null);

        if (this.client.type === clientType.NonClinical || this.providerSearchOptions.length === 0) {
          let isNoProviderSelected = false;
        if (this.client.physicianNPI)
          isNoProviderSelected = true;

          let noProviderOption = {
            disabled: false,
            group: null,
            selected: isNoProviderSelected,
            text: "No Provider Selected",
            value: "0"
          }

          this.providerSearchOptions.push(noProviderOption);

          this.defaultValue = parseInt(this.providerSearchOptions.indexOf(noProviderOption));
        }
      },

      onOrderLabsFirst() {
        Object.assign(this.report, {
          clientId: this.client.id,
          survey: {
            address: { ...this.patient.address },
            gender: this.patient.gender,
            birthDate: this.patient.birthDate,
            phoneNumber: this.patient.phoneNumber
          }
        });

        this.$router.push("../create/patient");
      },

      async onSubmit() {
        if (this.patient.pastReports) {
          let hasInitialAPOBReport = this.patient.pastReports.some(r =>
            (r.typeId === ProductType.InitialCRAAPOB || r.typeId === ProductType.RepeatedCRAAPOB) &&
            r.status === reportStatus.Completed
          );
          let hasInitialLDLPReport = this.patient.pastReports.some(r =>
            (r.typeId === ProductType.InitialCRALDLP || r.typeId === ProductType.RepeatedCRALDLP) &&
            r.status === reportStatus.Completed
          );

          if ((this.report.type !== ProductType.InitialDRA && this.report.type !== ProductType.RepeatedDRA) &&
              (hasInitialAPOBReport || hasInitialLDLPReport) &&
            ((this.report.type === ProductType.InitialCRALDLP || this.report.type === ProductType.RepeatedCRALDLP) && !hasInitialLDLPReport || (this.report.type === ProductType.InitialCRAAPOB || this.report.type === ProductType.RepeatedCRAAPOB) && !hasInitialAPOBReport))
          {
            this.$refs.confirmDifferentProductTypes.show();
          }
          else {
            this.confirmSubmit();
          }
        }
        else {
          this.confirmSubmit();
        }
      },

      async confirmSubmit() {
        this.report.clientId = this.client.id;
        await this.createReport();
        this.$root.successToast("Report created!");
        this.$router.push("../create/completed");
      },
    }
  }
 
</script>
<style lang="scss" scoped>
  @import '@/styles/mixins';

  .page {
    @include retina('/png/doctor.png');
  }

  .internal-labwork {
      margin: 2rem 0 2rem 0;
  }
</style>